import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../../Icons';
import './MobileBottomSheet.css';

const _openSheetInstanceIds = new Set();

/**
 * MobileBottomSheet Component
 * A native app-like slide-up bottom sheet for mobile devices
 *
 * @param {boolean} isOpen - Whether the sheet is open
 * @param {function} onClose - Callback when sheet should close: () => void
 * @param {ReactNode} children - Content to display in the sheet
 * @param {string} title - Optional title for the sheet header
 * @param {boolean} showHandle - Whether to show the drag handle (default: true)
 */
const MobileBottomSheet = ({
  isOpen = false,
  onClose,
  children,
  title,
  showHandle = true,
}) => {
  const instanceId = useId();
  const sheetRef = useRef(null);
  const startYRef = useRef(null);
  const currentYRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    _openSheetInstanceIds.add(instanceId);
    document.body.dataset.mobileBottomSheetOpen = 'true';
    return () => {
      _openSheetInstanceIds.delete(instanceId);
      if (_openSheetInstanceIds.size === 0) {
        delete document.body.dataset.mobileBottomSheetOpen;
      }
    };
  }, [isOpen, instanceId]);

  // Close on escape key and handle animation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sheet is open
      document.body.style.overflow = 'hidden';

      // Trigger animation by adding open class after a small delay
      if (sheetRef.current) {
        requestAnimationFrame(() => {
          if (sheetRef.current) {
            sheetRef.current.classList.add('open');
          }
        });
      }
    } else {
      // Remove open class when closing
      if (sheetRef.current) {
        sheetRef.current.classList.remove('open');
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle touch gestures for swipe-down to dismiss
  const handleTouchStart = (e) => {
    if (!showHandle) return;
    startYRef.current = e.touches[0].clientY;
    currentYRef.current = 0;
  };

  const handleTouchMove = (e) => {
    if (!startYRef.current || !showHandle) return;

    currentYRef.current = e.touches[0].clientY - startYRef.current;

    // Only allow downward swipes
    if (currentYRef.current > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${currentYRef.current}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!startYRef.current || !showHandle) return;

    // If swiped down more than 100px, close the sheet
    if (currentYRef.current > 100 && onClose) {
      onClose();
    } else if (sheetRef.current) {
      // Snap back to original position
      sheetRef.current.style.transform = 'translateY(0)';
    }

    startYRef.current = null;
    currentYRef.current = 0;
  };

  if (!isOpen) return null;

  const handleBackdropPointerDown = (e) => {
    e.stopPropagation();
  };

  const handleBackdropClick = (e) => {
    e.stopPropagation();
    if (onClose) onClose();
  };

  return createPortal(
    <>
      {/* Backdrop — portal avoids parent stacking contexts so nav/tabs cannot sit above the overlay */}
      <div
        className="mobile-bottom-sheet-overlay open"
        onPointerDown={handleBackdropPointerDown}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="mobile-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'mobile-bottom-sheet-title' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div
            className="mobile-bottom-sheet-handle"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="mobile-bottom-sheet-handle-bar" />
          </div>
        )}

        {/* Header */}
        {(title || showHandle) && (
          <div className="mobile-bottom-sheet-header">
            {title && (
              <h2 id="mobile-bottom-sheet-title" className="mobile-bottom-sheet-title">
                {title}
              </h2>
            )}
            <button
              className="mobile-bottom-sheet-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <Icons.X size={20} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="mobile-bottom-sheet-content">{children}</div>
      </div>
    </>,
    document.body
  );
};

export default MobileBottomSheet;
